import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

let toastIdCounter = 0;

function generateToastId(): string {
  toastIdCounter += 1;
  return `toast-${toastIdCounter}-${Date.now()}`;
}

export const DEFAULT_TOAST_DURATION = 3500;

// Bridge imperatif pour declencher un toast en dehors de l'arbre React
// (ex: l'intercepteur axios, qui n'est pas un composant et ne peut pas appeler useToast()).
let imperativeAddToast: ((toast: Omit<Toast, 'id'>) => void) | null = null;

export function showGlobalToast(toast: Omit<Toast, 'id'>) {
  imperativeAddToast?.(toast);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Le decompte d'auto-dismiss (avec pause au survol) vit dans ToastContainer/ToastItem —
  // ce hook se contente d'ajouter le toast a la liste.
  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = generateToastId();
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  imperativeAddToast = addToast;

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}